// Enhanced Offline Storage with Browser-Compatible Database Manager
import browserDatabaseManager from '../database/BrowserDatabaseManager.js';

class EnhancedOfflineStorage {
  constructor() {
    this.dbName = 'ACCTOO_ENHANCED_DB';
    this.version = 3; // Incremented for new business data collections
    this.db = null;
    this.deviceId = null;
    this.companyId = null;
    this.databaseManager = browserDatabaseManager;
  }

  // Initialize the database with device and company context
  async init(deviceId, companyId, userId = null) {
    this.deviceId = deviceId;
    this.companyId = companyId;
    
    // Validate inputs
    if (!deviceId) {
      console.error('❌ Device ID is required for offline storage initialization');
      throw new Error('Device ID is required for offline storage initialization');
    }
    
    if (!companyId) {
      console.warn('⚠️ Company ID is missing, using default context');
      this.companyId = 'default_company';
    }

    if (!userId) {
      console.warn('⚠️ User ID is missing, using device ID as user ID');
      userId = deviceId;
    }
    
    console.log('🔄 Initializing enhanced offline storage with browser database manager...', {
      deviceId: this.deviceId,
      companyId: this.companyId,
      userId: userId
    });
    
    try {
      // Initialize browser database manager
      await this.databaseManager.init(userId, companyId, deviceId);
      
      // Keep IndexedDB for backward compatibility
      if (window.indexedDB) {
        const request = indexedDB.open(this.dbName, this.version);
        
        return new Promise((resolve, reject) => {
          request.onerror = (event) => {
            console.error('❌ IndexedDB open failed:', request.error);
            reject(request.error);
          };
          
          request.onsuccess = () => {
            this.db = request.result;
            console.log('✅ Enhanced offline storage initialized successfully');
            resolve(this.db);
          };

          request.onupgradeneeded = (event) => {
            console.log('🔄 Upgrading IndexedDB schema...');
            const db = event.target.result;
            try {
              this.createStores(db);
              console.log('✅ IndexedDB schema upgraded successfully');
            } catch (error) {
              console.error('❌ Error creating stores:', error);
              reject(error);
            }
          };
        });
      } else {
        console.log('✅ Browser database manager initialized (IndexedDB not available)');
        return { success: true };
      }
    } catch (error) {
      console.error('❌ Enhanced offline storage initialization failed:', error);
      throw error;
    }
  }

  // Create object stores for enhanced offline functionality
  createStores(db) {
    // Device-specific data stores
    if (!db.objectStoreNames.contains('device_data')) {
      const deviceStore = db.createObjectStore('device_data', { keyPath: 'id' });
      deviceStore.createIndex('deviceId', 'deviceId', { unique: false });
      deviceStore.createIndex('companyId', 'companyId', { unique: false });
      deviceStore.createIndex('type', 'type', { unique: false });
    }

    // Core business data collections
    const coreCollections = ['parties', 'items', 'expenses', 'payments'];
    coreCollections.forEach(collection => {
      if (!db.objectStoreNames.contains(collection)) {
        const store = db.createObjectStore(collection, { keyPath: 'id' });
        store.createIndex('deviceId', 'deviceId', { unique: false });
        store.createIndex('companyId', 'companyId', { unique: false });
        store.createIndex('syncStatus', 'syncStatus', { unique: false });
        store.createIndex('lastModified', 'lastModified', { unique: false });
        
        // Specific indexes for parties
        if (collection === 'parties') {
          store.createIndex('partyType', 'partyType', { unique: false });
          store.createIndex('firmName', 'firmName', { unique: false });
        }
        
        // Specific indexes for items
        if (collection === 'items') {
          store.createIndex('itemName', 'itemName', { unique: false });
          store.createIndex('category', 'category', { unique: false });
        }
      }
    });

    // Sales related collections
    const salesCollections = ['salesBills', 'challan', 'quotation'];
    salesCollections.forEach(collection => {
      if (!db.objectStoreNames.contains(collection)) {
        const store = db.createObjectStore(collection, { keyPath: 'id' });
        store.createIndex('deviceId', 'deviceId', { unique: false });
        store.createIndex('companyId', 'companyId', { unique: false });
        store.createIndex('syncStatus', 'syncStatus', { unique: false });
        store.createIndex('lastModified', 'lastModified', { unique: false });
        
        // Business specific indexes
        store.createIndex('billNumber', 'billNumber', { unique: false });
        store.createIndex('billDate', 'billDate', { unique: false });
        store.createIndex('partyId', 'partyId', { unique: false });
        store.createIndex('paymentStatus', 'paymentStatus', { unique: false });
        store.createIndex('totalAmount', 'totalAmount', { unique: false });
      }
    });

    // Purchase related collections
    const purchaseCollections = ['purchaseBills', 'purchaseOrders'];
    purchaseCollections.forEach(collection => {
      if (!db.objectStoreNames.contains(collection)) {
        const store = db.createObjectStore(collection, { keyPath: 'id' });
        store.createIndex('deviceId', 'deviceId', { unique: false });
        store.createIndex('companyId', 'companyId', { unique: false });
        store.createIndex('syncStatus', 'syncStatus', { unique: false });
        store.createIndex('lastModified', 'lastModified', { unique: false });
        
        // Business specific indexes
        store.createIndex('purchaseNumber', 'purchaseNumber', { unique: false });
        store.createIndex('purchaseDate', 'purchaseDate', { unique: false });
        store.createIndex('supplierId', 'supplierId', { unique: false });
        store.createIndex('totalAmount', 'totalAmount', { unique: false });
      }
    });

    // Sync queue for offline operations
    if (!db.objectStoreNames.contains('sync_queue')) {
      const syncStore = db.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
      syncStore.createIndex('deviceId', 'deviceId', { unique: false });
      syncStore.createIndex('companyId', 'companyId', { unique: false });
      syncStore.createIndex('status', 'status', { unique: false });
      syncStore.createIndex('timestamp', 'timestamp', { unique: false });
    }

    // Conflict reports
    if (!db.objectStoreNames.contains('conflict_reports')) {
      const conflictStore = db.createObjectStore('conflict_reports', { keyPath: 'id', autoIncrement: true });
      conflictStore.createIndex('deviceId', 'deviceId', { unique: false });
      conflictStore.createIndex('companyId', 'companyId', { unique: false });
      conflictStore.createIndex('status', 'status', { unique: false });
      conflictStore.createIndex('timestamp', 'timestamp', { unique: false });
    }

    // Device series management
    if (!db.objectStoreNames.contains('device_series')) {
      const seriesStore = db.createObjectStore('device_series', { keyPath: 'id' });
      seriesStore.createIndex('deviceId', 'deviceId', { unique: true });
      seriesStore.createIndex('companyId', 'companyId', { unique: false });
    }

    // Local cache for fast access
    if (!db.objectStoreNames.contains('local_cache')) {
      const cacheStore = db.createObjectStore('local_cache', { keyPath: 'key' });
      cacheStore.createIndex('deviceId', 'deviceId', { unique: false });
      cacheStore.createIndex('companyId', 'companyId', { unique: false });
    }
  }

  // Generic CRUD operations with device context
  async add(storeName, data) {
    if (!this.databaseManager.isReady()) {
      await this.init(this.deviceId, this.companyId);
    }
    
    // Use browser database manager for main operations
    try {
      const result = await this.databaseManager.insert(storeName, data);
      console.log(`✅ Added record to ${storeName}:`, data.id);
      return result;
    } catch (error) {
      console.error(`❌ Add failed for ${storeName}:`, error);
      throw error;
    }
  }

  async get(storeName, id) {
    if (!this.databaseManager.isReady()) {
      await this.init(this.deviceId, this.companyId);
    }
    
    try {
      const records = await this.databaseManager.select(storeName, { id });
      return records.length > 0 ? records[0] : null;
    } catch (error) {
      console.error(`❌ Get failed for ${storeName}:`, error);
      throw error;
    }
  }

  async getAll(storeName, filters = {}) {
    if (!this.databaseManager.isReady()) {
      await this.init(this.deviceId, this.companyId);
    }
    
    try {
      return await this.databaseManager.select(storeName, filters);
    } catch (error) {
      console.error(`❌ GetAll failed for ${storeName}:`, error);
      throw error;
    }
  }

  async update(storeName, data) {
    if (!this.databaseManager.isReady()) {
      await this.init(this.deviceId, this.companyId);
    }
    
    try {
      const result = await this.databaseManager.update(storeName, data.id, data);
      console.log(`✅ Updated record in ${storeName}:`, data.id);
      return result;
    } catch (error) {
      console.error(`❌ Update failed for ${storeName}:`, error);
      throw error;
    }
  }

  async delete(storeName, id) {
    if (!this.databaseManager.isReady()) {
      await this.init(this.deviceId, this.companyId);
    }
    
    try {
      const result = await this.databaseManager.delete(storeName, id);
      console.log(`✅ Deleted record from ${storeName}:`, id);
      return result;
    } catch (error) {
      console.error(`❌ Delete failed for ${storeName}:`, error);
      throw error;
    }
  }

  // Device series management
  async getNextSeriesNumber(seriesType) {
    if (!this.db) await this.init(this.deviceId, this.companyId);
    
    const seriesKey = `${this.deviceId}_${seriesType}`;
    const seriesData = await this.get('device_series', seriesKey);
    
    if (seriesData) {
      seriesData.lastNumber += 1;
      await this.update('device_series', seriesData);
      return seriesData.lastNumber;
    } else {
      const newSeries = {
        id: seriesKey,
        deviceId: this.deviceId,
        companyId: this.companyId,
        seriesType,
        lastNumber: 1,
        createdAt: new Date()
      };
      await this.add('device_series', newSeries);
      return 1;
    }
  }

  // Generate device-specific invoice number
  async generateInvoiceNumber() {
    if (!this.databaseManager.isReady()) {
      await this.init(this.deviceId, this.companyId);
    }
    
    try {
      // Use browser database manager for bill number generation
      return await this.databaseManager.generateBillNumber('sales');
    } catch (error) {
      console.error('❌ Generate invoice number failed:', error);
      // Fallback to simple generation
      const seriesNumber = await this.getNextSeriesNumber('invoice');
      const year = new Date().getFullYear().toString().slice(-2);
      const nextYear = (parseInt(year) + 1).toString().slice(-2);
      return `INV${year}-${nextYear}/${seriesNumber.toString().padStart(4, '0')}`;
    }
  }

  // Sync queue management
  async addToSyncQueue(operation) {
    const syncItem = {
      ...operation,
      deviceId: this.deviceId,
      companyId: this.companyId,
      timestamp: new Date(),
      status: 'pending',
      retryCount: 0
    };
    return this.add('sync_queue', syncItem);
  }

  async getSyncQueue(filters = {}) {
    return this.getAll('sync_queue', {
      deviceId: this.deviceId,
      companyId: this.companyId,
      ...filters
    });
  }

  async updateSyncStatus(id, status, error = null) {
    const item = await this.get('sync_queue', id);
    if (item) {
      item.status = status;
      item.lastAttempt = new Date();
      if (error) item.lastError = error;
      return this.update('sync_queue', item);
    }
  }

  // Conflict detection and reporting
  async detectCustomerConflicts() {
    const customers = await this.getAll('parties', { companyId: this.companyId });
    const conflicts = [];
    
    // Group by similar names and phone numbers
    const groups = {};
    customers.forEach(customer => {
      const key = `${customer.name?.toLowerCase()}_${customer.phone}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(customer);
    });
    
    // Find conflicts
    Object.values(groups).forEach(group => {
      if (group.length > 1) {
        conflicts.push({
          type: 'customer_duplicate',
          data: group,
          severity: 'high',
          timestamp: new Date()
        });
      }
    });
    
    return conflicts;
  }

  async reportConflict(conflict) {
    const conflictReport = {
      ...conflict,
      deviceId: this.deviceId,
      companyId: this.companyId,
      timestamp: new Date(),
      status: 'pending_resolution'
    };
    return this.add('conflict_reports', conflictReport);
  }

  // Local cache for fast access
  async setCache(key, data, ttl = 3600000) { // 1 hour default TTL
    const cacheItem = {
      key,
      data,
      deviceId: this.deviceId,
      companyId: this.companyId,
      timestamp: new Date(),
      ttl
    };
    return this.add('local_cache', cacheItem);
  }

  async getCache(key) {
    const cacheItem = await this.get('local_cache', key);
    if (cacheItem) {
      const now = new Date();
      const age = now - cacheItem.timestamp;
      if (age < cacheItem.ttl) {
        return cacheItem.data;
      } else {
        // Cache expired, remove it
        await this.delete('local_cache', key);
      }
    }
    return null;
  }

  // Data compression and cleanup
  async compressData() {
    // Implementation for data compression
    // This would compress old data to save space
    console.log('Data compression not implemented yet');
  }

  async cleanupOldData(daysOld = 30) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    
    const collections = ['parties', 'sales', 'purchases', 'payments', 'items', 'expenses'];
    
    for (const collection of collections) {
      const items = await this.getAll(collection, { companyId: this.companyId });
      const oldItems = items.filter(item => 
        item.lastModified && new Date(item.lastModified) < cutoffDate
      );
      
      for (const item of oldItems) {
        await this.delete(collection, item.id);
      }
    }
  }

  // Get storage usage statistics
  async getStorageStats() {
    if (!this.db) await this.init(this.deviceId, this.companyId);
    
    const collections = ['parties', 'sales', 'purchases', 'payments', 'items', 'expenses', 'sync_queue', 'conflict_reports'];
    const stats = {};
    
    for (const collection of collections) {
      const items = await this.getAll(collection, { companyId: this.companyId });
      stats[collection] = {
        count: items.length,
        size: JSON.stringify(items).length
      };
    }
    
    return stats;
  }

  // Clear all data for logout
  async clearAll() {
    if (!this.db) await this.init(this.deviceId, this.companyId);
    const collections = ['parties', 'sales', 'purchases', 'payments', 'items', 'expenses', 'sync_queue', 'conflict_reports', 'device_series', 'local_cache'];
    
    return Promise.all(
      collections.map(collection => {
        return new Promise((resolve, reject) => {
          const transaction = this.db.transaction([collection], 'readwrite');
          const store = transaction.objectStore(collection);
          const request = store.clear();

          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      })
    );
  }

  // Sync company data to local storage for offline use
  async syncCompanyData() {
    try {
      console.log('🔄 Syncing company data for offline use...');
      console.log('Company ID:', this.companyId);
      console.log('Device ID:', this.deviceId);

      if (!this.companyId) {
        return { success: false, error: 'No company ID available' };
      }

      if (!this.databaseManager.isReady()) {
        await this.init(this.deviceId, this.companyId);
      }

      // Use browser database manager for sync
      const syncResult = await this.databaseManager.sync();
      
      if (syncResult.success) {
        // Store sync timestamp
        await this.setCache('last_sync_timestamp', new Date().toISOString(), 86400000); // 24 hours
        console.log('✅ Company data synced successfully for offline use');
        return { success: true, message: 'Data synced for offline use' };
      } else {
        console.error('❌ Sync failed:', syncResult.error);
        return { success: false, error: syncResult.error };
      }
    } catch (error) {
      console.error('Error syncing company data:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper function to sync specific data type
  async syncData(dataType, collectionRef) {
    try {
      console.log(`🔄 Syncing ${dataType} data...`);
      
      // Import getDocs locally
      const { getDocs } = await import('firebase/firestore');
      
      const snapshot = await getDocs(collectionRef);
      console.log(`📊 Found ${snapshot.docs.length} ${dataType} documents in Firebase`);
      
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastUpdated: new Date(),
        syncedAt: new Date()
      }));

      // Store in IndexedDB
      console.log(`💾 Storing ${data.length} ${dataType} records in IndexedDB...`);
      await this.storeData(dataType, data);
      
      console.log(`✅ Synced ${data.length} ${dataType} records`);
      return { success: true, count: data.length };
    } catch (error) {
      console.error(`❌ Error syncing ${dataType}:`, error);
      return { success: false, error: error.message };
    }
  }

  // Clear all data for a specific data type (simple approach)
  async clearAllDataForType(dataType) {
    try {
      console.log(`🧹 Clearing all data for ${dataType}`);
      
      const transaction = this.db.transaction([dataType], 'readwrite');
      const store = transaction.objectStore(dataType);
      
      const clearRequest = store.clear();
      await new Promise((resolve, reject) => {
        clearRequest.onsuccess = () => {
          console.log(`✅ Cleared all data for ${dataType}`);
          resolve();
        };
        clearRequest.onerror = () => {
          console.error(`❌ Error clearing ${dataType}:`, clearRequest.error);
          reject(clearRequest.error);
        };
      });
    } catch (error) {
      console.warn(`⚠️ Could not clear data for ${dataType}:`, error);
      // Don't throw error, just log warning
    }
  }

  // Complete IndexedDB reset - Delete entire database and recreate
  async resetIndexedDB() {
    try {
      console.log('🔄 Starting complete IndexedDB reset...');
      
      // Close current database connection
      if (this.db) {
        this.db.close();
        this.db = null;
      }
      
      // Delete the entire database
      const deleteRequest = indexedDB.deleteDatabase(this.dbName);
      await new Promise((resolve, reject) => {
        deleteRequest.onsuccess = () => {
          console.log('✅ Database deleted successfully');
          resolve();
        };
        deleteRequest.onerror = () => {
          console.error('❌ Error deleting database:', deleteRequest.error);
          reject(deleteRequest.error);
        };
        deleteRequest.onblocked = () => {
          console.warn('⚠️ Database deletion blocked, trying again...');
          // Try again after a short delay
          setTimeout(() => {
            deleteRequest.onsuccess = () => resolve();
            deleteRequest.onerror = () => reject(deleteRequest.error);
          }, 1000);
        };
      });
      
      // Wait a moment for cleanup
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reinitialize the database
      await this.init(this.deviceId, this.companyId);
      
      console.log('✅ IndexedDB reset completed successfully');
      return { success: true, message: 'IndexedDB reset completed' };
    } catch (error) {
      console.error('❌ Error resetting IndexedDB:', error);
      return { success: false, error: error.message };
    }
  }

  // Clear all data from all stores (alternative to complete reset)
  async clearAllData() {
    try {
      console.log('🧹 Clearing all data from all stores...');
      
      const collections = [
        // Core business data
        'parties', 'items', 'expenses', 'payments',
        // Sales related
        'salesBills', 'challan', 'quotation',
        // Purchase related
        'purchaseBills', 'purchaseOrders',
        // System data
        'sync_queue', 'conflict_reports', 'device_series', 'local_cache', 'device_data'
      ];
      
      for (const collection of collections) {
        try {
          const transaction = this.db.transaction([collection], 'readwrite');
          const store = transaction.objectStore(collection);
          
          const clearRequest = store.clear();
          await new Promise((resolve, reject) => {
            clearRequest.onsuccess = () => {
              console.log(`✅ Cleared ${collection}`);
              resolve();
            };
            clearRequest.onerror = () => {
              console.warn(`⚠️ Could not clear ${collection}:`, clearRequest.error);
              resolve(); // Continue with other collections
            };
          });
        } catch (error) {
          console.warn(`⚠️ Error clearing ${collection}:`, error);
        }
      }
      
      console.log('✅ All data cleared successfully');
      return { success: true, message: 'All data cleared' };
    } catch (error) {
      console.error('❌ Error clearing all data:', error);
      return { success: false, error: error.message };
    }
  }

  // Clear existing data for specific company and device
  async clearCompanyData(dataType) {
    try {
      console.log(`🧹 Clearing existing data for ${dataType} (company: ${this.companyId}, device: ${this.deviceId})`);
      
      // Use a separate transaction for reading
      const readTransaction = this.db.transaction([dataType], 'readonly');
      const readStore = readTransaction.objectStore(dataType);
      
      // Get all existing data
      const getAllRequest = readStore.getAll();
      const existingData = await new Promise((resolve, reject) => {
        getAllRequest.onsuccess = () => resolve(getAllRequest.result);
        getAllRequest.onerror = () => reject(getAllRequest.error);
      });
      
      // Filter data for this company and device
      const dataToDelete = existingData.filter(item => 
        item.companyId === this.companyId && item.deviceId === this.deviceId
      );
      
      console.log(`🗑️ Found ${dataToDelete.length} existing records to delete for ${dataType}`);
      
      if (dataToDelete.length === 0) {
        console.log(`✅ No existing records to clear for ${dataType}`);
        return;
      }
      
      // Use a separate transaction for deleting
      const deleteTransaction = this.db.transaction([dataType], 'readwrite');
      const deleteStore = deleteTransaction.objectStore(dataType);
      
      // Delete each record
      for (const item of dataToDelete) {
        await new Promise((resolve, reject) => {
          const deleteRequest = deleteStore.delete(item.id);
          deleteRequest.onsuccess = () => resolve();
          deleteRequest.onerror = () => reject(deleteRequest.error);
        });
      }
      
      console.log(`✅ Cleared ${dataToDelete.length} existing records for ${dataType}`);
    } catch (error) {
      console.warn(`⚠️ Could not clear existing data for ${dataType}:`, error);
      // Don't throw error, just log warning
    }
  }

  // Store data in IndexedDB
  async storeData(dataType, data) {
    try {
      console.log(`🔄 Starting storeData for ${dataType} with ${data.length} records`);
      console.log('Database status:', this.db ? 'Initialized' : 'Not initialized');
      console.log('Device ID:', this.deviceId);
      console.log('Company ID:', this.companyId);
      
      if (!this.db) {
        throw new Error('Database not initialized');
      }

      // Clear all data for this dataType to avoid conflicts
      await this.clearAllDataForType(dataType);

      console.log(`📦 Creating transaction for ${dataType}`);
      const transaction = this.db.transaction([dataType], 'readwrite');
      const store = transaction.objectStore(dataType);

      console.log(`➕ Adding ${data.length} new records to ${dataType}`);
      // Add new data
      for (let i = 0; i < data.length; i++) {
        const item = data[i];
        const itemWithContext = {
          ...item,
          deviceId: this.deviceId,
          companyId: this.companyId,
          syncStatus: 'synced',
          lastModified: new Date()
        };
        
        console.log(`📝 Adding item ${i + 1}/${data.length} to ${dataType}:`, itemWithContext.id);
        
        // Use put instead of add to handle existing keys gracefully
        await new Promise((resolve, reject) => {
          const putRequest = store.put(itemWithContext);
          putRequest.onsuccess = () => {
            console.log(`✅ Added/Updated item ${i + 1}/${data.length} to ${dataType}`);
            resolve();
          };
          putRequest.onerror = () => {
            console.error(`❌ Error adding/updating item ${i + 1}/${data.length} to ${dataType}:`, putRequest.error);
            reject(putRequest.error);
          };
        });
      }

      console.log(`💾 Successfully stored ${data.length} ${dataType} records in IndexedDB`);
    } catch (error) {
      console.error(`❌ Error storing ${dataType} data:`, error);
      throw error;
    }
  }
}

// Create singleton instance
const enhancedOfflineStorage = new EnhancedOfflineStorage();

export default enhancedOfflineStorage;
